// hooks/useSubtasksMediaOverview.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { RootState } from 'constants/interfaces'
import { getSubtasksResultsOverview } from 'redux/actions/tasks_v2'
import { ICurrentTaskInsight } from '../Insights/InsightsStackProvider'
import { getMediaGalleryResults } from '../Insights/model/mediaGallery'

export const useSubtasksMediaOverview = (
  task: ICurrentTaskInsight,
  contextIds: string[],
  recurrenceTs: string | null,
  subtaskIds: string[]
) => {
  const { tid, recurrence_frequency, recurrence_interval } = task

  const dispatch: AppDispatch = useDispatch()

  const { allResults } = useSelector((state: RootState) => state.tasks_v2)

  const mediaResults = useMemo(
    () => getMediaGalleryResults(allResults[tid] || [], recurrenceTs),
    [allResults, tid, recurrenceTs]
  )

  const [displayedContexts, setDisplayedContexts] = useState<string[]>([])
  const [loadingContexts, setLoadingContexts] = useState<{ [contextId: string]: boolean }>({})

  const pageSize = 10

  const currentPageRef = useRef(0)
  const isMountedRef = useRef(true)
  const isLoadingPageRef = useRef(false)
  const loadedContextIdsRef = useRef<Set<string>>(new Set())
  const overviewRunIdRef = useRef(0)

  const contextIdsKey = useMemo(() => contextIds.join('|'), [contextIds])
  const subtaskIdsKey = useMemo(() => subtaskIds.join('|'), [subtaskIds])

  const getContextIdsFromKey = useCallback(() => (contextIdsKey ? contextIdsKey.split('|') : []), [contextIdsKey])

  const getSubtaskIdsFromKey = useCallback(() => (subtaskIdsKey ? subtaskIdsKey.split('|') : []), [subtaskIdsKey])

  const setContextsLoading = useCallback((contextsToUpdate: string[], isLoading: boolean) => {
    setLoadingContexts((prev) => {
      const newLoadingContexts = { ...prev }

      contextsToUpdate.forEach((contextId) => {
        newLoadingContexts[contextId] = isLoading
      })
      return newLoadingContexts
    })
  }, [])

  const loadMoreData = useCallback(() => {
    if (isLoadingPageRef.current) return

    const allContextIds = getContextIdsFromKey()
    const nextPage = currentPageRef.current + 1
    const offset = nextPage * pageSize
    const contextsToLoad = allContextIds.slice(offset, offset + pageSize)

    if (contextsToLoad.length === 0) {
      return
    }

    const contextsToFetch = contextsToLoad.filter(
      (contextId) =>
        !loadedContextIdsRef.current.has(contextId) && !mediaResults.some((result) => result.context_id === contextId)
    )

    setDisplayedContexts((prevContexts) => Array.from(new Set([...prevContexts, ...contextsToLoad])))
    currentPageRef.current = nextPage

    if (contextsToFetch.length === 0) {
      loadedContextIdsRef.current = new Set([...Array.from(loadedContextIdsRef.current), ...contextsToLoad])
      return
    }

    isLoadingPageRef.current = true

    const runId = overviewRunIdRef.current

    setContextsLoading(contextsToFetch, true)

    dispatch(
      getSubtasksResultsOverview({
        tid,
        recurrence_interval,
        sub_tasks_ids: getSubtaskIdsFromKey(),
        recurrence_ts: recurrenceTs,
        recurrence_frequency,
        contextIds: contextsToFetch,
        shouldApply: () => isMountedRef.current && runId === overviewRunIdRef.current,
      })
    )
      .then(() => {
        if (!isMountedRef.current || runId !== overviewRunIdRef.current) return

        loadedContextIdsRef.current = new Set([...Array.from(loadedContextIdsRef.current), ...contextsToFetch])
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        if (!isMountedRef.current || runId !== overviewRunIdRef.current) return

        setContextsLoading(contextsToFetch, false)
        isLoadingPageRef.current = false
      })
  }, [
    dispatch,
    getContextIdsFromKey,
    getSubtaskIdsFromKey,
    mediaResults,
    recurrence_frequency,
    recurrence_interval,
    recurrenceTs,
    setContextsLoading,
    tid,
  ])

  const hasMore = displayedContexts.length < contextIds.length

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      isLoadingPageRef.current = false
    }
  }, [])

  useEffect(() => {
    overviewRunIdRef.current += 1

    const runId = overviewRunIdRef.current
    const contextsToLoad = getContextIdsFromKey().slice(0, pageSize)

    currentPageRef.current = 0
    isLoadingPageRef.current = false
    loadedContextIdsRef.current.clear()
    setDisplayedContexts(contextsToLoad)
    setLoadingContexts({})

    if (contextsToLoad.length === 0) return

    isLoadingPageRef.current = true
    setContextsLoading(contextsToLoad, true)

    dispatch(
      getSubtasksResultsOverview({
        tid,
        recurrence_interval,
        sub_tasks_ids: getSubtaskIdsFromKey(),
        recurrence_ts: recurrenceTs,
        recurrence_frequency,
        contextIds: contextsToLoad,
        shouldApply: () => isMountedRef.current && runId === overviewRunIdRef.current,
      })
    )
      .then(() => {
        if (!isMountedRef.current || runId !== overviewRunIdRef.current) return

        loadedContextIdsRef.current = new Set([...Array.from(loadedContextIdsRef.current), ...contextsToLoad])
      })
      .catch((err) => {
        console.error(err)
      })
      .finally(() => {
        if (!isMountedRef.current || runId !== overviewRunIdRef.current) return

        setContextsLoading(contextsToLoad, false)
        isLoadingPageRef.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    getContextIdsFromKey,
    getSubtaskIdsFromKey,
    recurrence_frequency,
    recurrence_interval,
    recurrenceTs,
    setContextsLoading,
    tid,
  ])

  return {
    displayedContexts,
    mediaResults,
    loadMoreData,
    hasMore,
    loadingContexts,
  }
}
